import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';

import { RouteExtras } from '../../interfaces/route-extras.interface';
import { OrderStore } from '../../order-store';
import { ApiService } from '../../services/api.service';

@Component({
  selector: 'app-address-form',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
  ],
  templateUrl: './address-form.html',
  styleUrl: './address-form.scss',
})
export class AddressForm {
  private readonly formBuilder = inject(FormBuilder);
  private readonly store = inject(OrderStore);
  private readonly router = inject(Router);
  private readonly apiService = inject(ApiService);

  private readonly routeExtras: RouteExtras =
    (this.router.getCurrentNavigation()?.extras.state as RouteExtras) ?? {};

  readonly formGroup = this.formBuilder.group({
    country: ['', [Validators.required, Validators.maxLength(256)]],
    city: ['', [Validators.required, Validators.maxLength(256)]],
    area: ['', [Validators.required, Validators.maxLength(256)]],
    buildingNumber: ['', [Validators.required, Validators.maxLength(256)]],
    apartmentNumber: ['', [Validators.required, Validators.maxLength(256)]],
    makaniNumber: ['', [Validators.maxLength(256)]],
    name: ['', [Validators.maxLength(256)]],
    location: ['', [Validators.required, Validators.maxLength(256)]],
  });

  constructor() {
    const confirmedData = this.store.confirmedData();
    const addressToSave = confirmedData?.addressToSave;
    if (addressToSave) {
      this.formGroup.patchValue({
        country: addressToSave.country,
        city: addressToSave.city,
        buildingNumber: addressToSave.buildingNumber,
        apartmentNumber: addressToSave.apartmentNumber,
        makaniNumber: addressToSave.makaniNumber,
        name: addressToSave.name,
        location: addressToSave.location
          ? `${addressToSave.location.latitude},${addressToSave.location.longitude}`
          : '',
      });
    }
    if (confirmedData?.address) {
      this.formGroup.controls.area.setValue(confirmedData.address);
    }
  }

  public save(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }
    const data = this.formGroup.getRawValue();
    let area = `${data.area}, Buidling ${data.buildingNumber}, Apartment ${data.apartmentNumber}`;
    if (data.makaniNumber) {
      area += `, makani number ${data.makaniNumber}`;
    }
    if (data.name) {
      this.store.setAddressToSave({
        country: data.country ?? '',
        city: data.city ?? '',
        area,
        buildingNumber: data.buildingNumber ?? '',
        apartmentNumber: data.apartmentNumber ?? '',
        makaniNumber: data.makaniNumber ?? '',
        name: data.name as 'Office' | 'Home',
      });
    } else {
      this.store.removeAddressToSave();
    }
    this.store.setConfirmedData({ address: area });
    if (this.routeExtras.submit) {
      this.apiService.confirmData();
    }
    this.route(this.routeExtras.backToMain ? 'main' : 'time');
  }

  public backToMap(): void {
    this.route('map');
  }

  private route(type: 'time' | 'map' | 'main'): void {
    this.router.navigate([type], {
      queryParamsHandling: 'merge',
      state: this.routeExtras,
    });
  }
}
